/**
 * The saved 3PL blank choice on a product row (Phase 92, UI-SPEC 1.3, 1.4, 1.5).
 *
 *   - `SupplySavedSummary`: Tier 1's read-only body for a product whose scan
 *     summary says it is built on a 3PL's blank. Saved, not yet synced: the
 *     blank chip, the anatomy, "{ready} ready · {held} held back", the stock
 *     note and the saved note. Synced: "Printed on {owner}'s blank", the
 *     anatomy, the variant table, both notes and the synced note (synced
 *     variants keep their blank; the others can still change).
 *   - `SupplySavedActions`: the TierCard `right` slot: ghost "Change blank"
 *     and, while nothing is synced, orange outline "Stop using this blank"
 *     (AlertDialog). Admins only. BLU-1522 S2b: a synced product keeps
 *     "Change blank" (the composer locks its synced rows) and has no "Stop
 *     using this blank" (the clear refuses a product with a synced variant).
 *   - `SupplyRowChip`: the header's "3PL blank" ring chip (amber when a blank
 *     needs attention) with sr-only "Prints on {owner}'s blank".
 *   - `useSupplyElection`: the expanded row's one `getSupplyElection` read.
 *
 * Reads: the row mounts this only for a product whose scan summary carries
 * `supply` (the page's existing scan read, zero added there). An admin's
 * expanded row makes one `getSupplyElection` (30 s stale) and reuses the
 * `listSharedStockOwners` query Tier 1 and Tier 4 already make. A non-admin
 * makes no read: the summary renders from the scan summary alone, with no
 * owner name (the server would not give one without SkusManage anyway).
 *
 * BLU-1522: a product on several base garments lists each garment with the
 * colours (or sizes) on it, and the anatomy's type segment changes per
 * variant. A non-admin hears only how many base garments it uses.
 *
 * The anatomy here is the client's OWN saved blank code (its snapshot, not
 * the 3PL's code tables) plus its own design code, so nothing server-side is
 * decoded in the browser (D-15). No other store is ever named (D-07).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, Boxes, CheckCircle2, Info, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

import { SkuAnatomy, type AnatomySegment } from '../SkuAnatomy';
import { GarmentThumb } from '../GarmentThumb';
import { electionGarmentSplit, garmentKeyOfCode, type SupplyElection } from '../../hooks/useSupplyComposer';
import type { PreviewProduct } from '../../lib/types';

/** The scan's per-product supply summary (92-23), present only for a product on a 3PL's blank. */
export type ScannedSupply = NonNullable<PreviewProduct['supply']>;

export const SUPPLY_ELECTION_STALE_MS = 30_000;
/** Above this many rows the synced variant table scrolls inside its own box (UI 22). */
const TABLE_BOX_ROWS = 12;

/** The saved choice for one product; `enabled: false` makes no request. */
export function useSupplyElection(productId: string, enabled: boolean) {
  return trpc.productSync.getSupplyElection.useQuery(
    { productId },
    { staleTime: SUPPLY_ELECTION_STALE_MS, enabled },
  );
}

/** "{ready} ready · {held} held back": elected variants, and every other variant of the product. */
function supplySavedCounts(
  product: Pick<PreviewProduct, 'variantCount'>,
  summary: Pick<ScannedSupply, 'electedVariantCount'>,
  election: SupplyElection | null,
): { ready: number; held: number } {
  const ready = election ? election.variants.length : summary.electedVariantCount;
  return { ready, held: Math.max(0, product.variantCount - ready) };
}

/** The election's variants in the product's own order. */
function electedRows(product: Pick<PreviewProduct, 'variants'>, election: SupplyElection) {
  const byId = new Map(election.variants.map((v) => [v.variantId, v]));
  const rows: Array<{ variant: PreviewProduct['variants'][number]; elected: SupplyElection['variants'][number] }> = [];
  for (const variant of product.variants) {
    const elected = byId.get(variant.id);
    if (elected) rows.push({ variant, elected });
  }
  return rows;
}

/**
 * The anatomy of one saved print SKU: the blank code's own segments (type,
 * size when the blank has one, colour) plus the product's design code and the
 * client's light or dark choice for that colour.
 */
function savedAnatomy(
  t: TFunction<'productSync'>,
  product: Pick<PreviewProduct, 'variants' | 'designCode'>,
  election: SupplyElection,
  customProduct: boolean,
  manyGarments: boolean,
): AnatomySegment[] | null {
  const sample = electedRows(product, election)[0]?.elected ?? election.variants[0];
  if (!sample) return null;
  const parts = sample.blankCode.trim().toUpperCase().split('-');
  if (parts.length !== 2 && parts.length !== 3) return null;
  const segments: AnatomySegment[] = [
    { key: 'type', text: parts[0]!, label: t('anatomy.type'), ...(manyGarments ? { tone: 'dynamic' as const } : {}) },
  ];
  if (parts.length === 3) segments.push({ key: 'size', text: parts[1]!, label: t('anatomy.size'), tone: 'dynamic' });
  segments.push({ key: 'colour', text: parts[parts.length - 1]!, label: t('anatomy.colour'), tone: 'dynamic' });
  const design = (product.designCode ?? '').trim().toUpperCase();
  if (design) segments.push({ key: 'design', text: `${design}${sample.isDark ? 'D' : 'L'}`, label: customProduct ? t('anatomy.auto') : t('anatomy.design') });
  return segments;
}

function variantLabel(t: TFunction<'productSync'>, variant: PreviewProduct['variants'][number]): string {
  const parts = [variant.sizeName, variant.colourName].filter((p): p is string => typeof p === 'string' && p.trim() !== '');
  return parts.length > 0 ? parts.join(' / ') : t('supply.preview.onlyVariant');
}

// ---------------------------------------------------------------------------
// Tier 1 body
// ---------------------------------------------------------------------------

export interface SupplySavedSummaryProps {
  product: PreviewProduct;
  summary: ScannedSupply;
  /** The saved choice, or null (still loading, failed, or a non-admin who makes no read). */
  election: SupplyElection | null;
  electionLoading: boolean;
  electionError: boolean;
  onRetry?: () => void;
  /** The 3PL's name, or null when the caller cannot see it. */
  ownerName: string | null;
  /** A sync already wrote at least one variant: the choice can no longer change. */
  locked: boolean;
  admin: boolean;
  /**
   * BLU-1522 S2b: this viewer can open "Change blank" (an admin, the saved
   * choice loaded, the 3PL's blanks still on for this store). Only then does
   * the synced note say the unsynced variants can still change.
   */
  canChange?: boolean;
  /** One plain line from the last action (for example a refused Stop using). */
  notice?: string | null;
  /** The product is custom (the row's decision): no light or dark column, and the last segment is made automatically. */
  customProduct?: boolean;
}

export function SupplySavedSummary({
  product,
  summary,
  election,
  electionLoading,
  electionError,
  onRetry,
  ownerName,
  locked,
  admin,
  canChange = false,
  notice = null,
  customProduct = false,
}: SupplySavedSummaryProps) {
  const { t } = useTranslation('productSync');
  const counts = supplySavedCounts(product, summary, election);
  // Only garments the product's current variants use: the list below names
  // each one, so the count never says more than it lists. A non-admin has the
  // scan's count, which reads the current variants too.
  const garmentSplit = election ? electionGarmentSplit(election, product) : null;
  const garmentCount = election ? (garmentSplit?.valuesByGarment.size ?? 1) : (summary.garmentKeys?.length ?? 1);
  const manyGarments = garmentCount > 1;
  // One garment left: the one the current variants use, by its own title.
  const currentCode = election && !manyGarments ? electedRows(product, election)[0]?.elected.blankCode : undefined;
  const current = currentCode
    ? election?.garments?.find((g) => g.garmentKey.toUpperCase() === garmentKeyOfCode(currentCode))
    : undefined;
  const garmentTitle = manyGarments
    ? t('supply.saved.garmentCount', { count: garmentCount })
    : current?.title?.trim() || election?.garmentTitle?.trim() || summary.garmentKey;
  const imageUrl = manyGarments ? null : current ? current.imageUrl : (election?.garmentImageUrl ?? null);
  const anatomy = election ? savedAnatomy(t, product, election, customProduct, manyGarments) : null;
  const locations = (election?.locations ?? []).map((l) => l.name).join(t('supply.preview.listSeparator'));
  const rows = locked && election ? electedRows(product, election) : [];
  const boxed = rows.length > TABLE_BOX_ROWS;

  const table =
    rows.length > 0 ? (
      <table className="w-full border-collapse text-xs">
        <caption className="sr-only">{t('supply.saved.tableLabel')}</caption>
        <thead>
          <tr className="text-left text-muted-foreground">
            {[t('supply.preview.colVariant'), t('supply.preview.colBlank'), ...(customProduct ? [] : [t('supply.match.design')])].map((heading) => (
              <th key={heading} scope="col" className={cn('py-2 pr-3 font-medium', boxed && 'sticky top-0 z-10 bg-background')}>
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ variant, elected }) => {
            const live = [elected.blankSize, elected.blankColour].filter((p): p is string => !!p && p.trim() !== '').join(' · ');
            return (
              <tr key={variant.id} className="border-t align-top" data-testid="supply-saved-row">
                <td className="py-2 pr-3 font-medium">
                  <span className="break-words">{variantLabel(t, variant)}</span>
                </td>
                <td className="min-w-0 py-2 pr-3">
                  <span className="font-mono break-all">{elected.blankCode}</span>
                  {live && <span className="block break-words text-muted-foreground">{live}</span>}
                </td>
                {!customProduct && (
                  <td className="py-2 pr-3 text-muted-foreground">
                    {elected.isDark ? t('supply.design.dark') : t('supply.design.light')}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    ) : null;

  return (
    <div className="flex flex-col gap-3 motion-safe:animate-fade-in" data-testid={locked ? 'supply-synced' : 'supply-saved-summary'}>
      <div className="flex min-w-0 items-center gap-3">
        {imageUrl && <GarmentThumb src={imageUrl} className="border" />}
        <div className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
            <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
            <span className="break-words">
              {locked
                ? ownerName
                  ? t('supply.saved.printedOn', { owner: ownerName })
                  : t('supply.saved.printedOnUnknown')
                : garmentTitle}
            </span>
          </span>
          {locked && <span className="truncate text-xs text-muted-foreground" title={garmentTitle}>{garmentTitle}</span>}
        </div>
      </div>

      {election && garmentSplit && (
        <ul className="flex flex-col gap-2" aria-label={t('supply.saved.garmentsLabel')} data-testid="supply-saved-garments">
          {[...garmentSplit.valuesByGarment].map(([key, values]) => {
            const garment = election.garments?.find((g) => g.garmentKey.toUpperCase() === key);
            const list = values.join(t('supply.preview.listSeparator'));
            return (
              <li key={key} className="flex min-w-0 items-start gap-2 text-xs">
                <GarmentThumb src={garment?.imageUrl} size="sm" className="border" />
                <span className="flex min-w-0 flex-col">
                  <span className="break-words font-medium">{garment?.title ?? key}</span>
                  <span className="break-words text-muted-foreground">
                    {garmentSplit.split === 'colour'
                      ? t('supply.saved.garmentColours', { values: list })
                      : t('supply.saved.garmentSizes', { values: list })}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {summary.attention && (
        <NoteLine icon={AlertTriangle} tone="warning" className="leading-normal">
          {ownerName ? t('supply.refusal.blankNotOffered', { owner: ownerName }) : t('job.blocker.supply_blank_not_offered')}
        </NoteLine>
      )}

      {admin && electionError && !election ? (
        <SectionError
          variant="compact"
          body={t('supply.saved.loadError')}
          onRetry={onRetry ?? (() => undefined)}
          retryLabel={t('supply.saved.retry')}
        />
      ) : admin && electionLoading && !election ? (
        <div className="flex flex-wrap gap-2" aria-hidden="true" data-testid="supply-saved-skeleton">
          <Skeleton className="h-12 w-16 motion-reduce:animate-none" />
          <Skeleton className="h-12 w-12 motion-reduce:animate-none" />
          <Skeleton className="h-12 w-12 motion-reduce:animate-none" />
          <Skeleton className="h-12 w-20 motion-reduce:animate-none" />
        </div>
      ) : anatomy ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">{t('tier1.everyVariant')}</p>
          <SkuAnatomy size="md" segments={anatomy} />
        </div>
      ) : null}

      <p className="text-xs tabular-nums text-muted-foreground" data-testid="supply-saved-counts">
        {t('supply.saved.counts', { ready: counts.ready, held: counts.held })}
      </p>

      {table &&
        (boxed ? (
          // A scrolling region is a tab stop so the keyboard can scroll it.
          <div
            role="region"
            aria-label={t('supply.saved.tableLabel')}
            tabIndex={0}
            className="max-h-80 overflow-y-auto rounded-md border px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {table}
          </div>
        ) : (
          table
        ))}

      {locked && ownerName && (
        <NoteLine icon={Link2} className="leading-normal">
          {t('supply.note.inherits', { owner: ownerName })}
        </NoteLine>
      )}
      {ownerName && locations && (
        <NoteLine icon={Boxes} className="leading-normal">
          {t('supply.note.stock', { owner: ownerName, location: locations })}
        </NoteLine>
      )}
      {locked ? (
        <NoteLine icon={Info} className="leading-normal">
          {canChange ? t('supply.syncedNote') : t('supply.syncedNoteReadOnly')}
        </NoteLine>
      ) : (
        admin && (
          <NoteLine icon={CheckCircle2} className="leading-normal">
            {t('supply.savedNote')}
          </NoteLine>
        )
      )}
      {notice && (
        <NoteLine icon={AlertTriangle} tone="warning" className="leading-normal">
          {notice}
        </NoteLine>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier 1 right slot: Change blank, Stop using this blank
// ---------------------------------------------------------------------------

export interface SupplySavedActionsProps {
  productId: string;
  ownerName: string | null;
  /** "Change blank" needs the saved choice and a 3PL that still lets this store use its blanks. */
  canChange: boolean;
  /** BLU-1522 S2b: false while any variant is synced (default true): no "Stop using this blank". */
  canStop?: boolean;
  onChangeBlank: () => void;
  /** The choice was removed: the row goes back to "My own blanks". */
  onCleared: () => void;
  /** The server says a sync already wrote this product, so it can no longer stop. */
  onAlreadySynced: () => void;
}

export function SupplySavedActions({
  productId,
  ownerName,
  canChange,
  canStop = true,
  onChangeBlank,
  onCleared,
  onAlreadySynced,
}: SupplySavedActionsProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const clear = trpc.productSync.clearSupplyElection.useMutation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);

  const refresh = () => {
    void utils.productSync.getProductSyncPreview.invalidate();
    void utils.productSync.getSupplyElection.invalidate({ productId });
  };

  const stop = async () => {
    // A second click while one is in flight never sends twice.
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      const result = await clear.mutateAsync({ productId });
      if (result.ok) {
        // No toast (DESIGN.md §15.4): the tier changes state.
        refresh();
        setOpen(false);
        onCleared();
      } else if (result.refusal === 'already_synced') {
        refresh();
        setOpen(false);
        onAlreadySynced();
      } else if (result.refusal === 'sync_running') {
        // BLU-1522 S2b: nothing was removed; the dialog stays for a retry later.
        toast.error(t('supply.refusal.syncRunning'), { id: `supply-stop-${productId}` });
      } else {
        toast.error(t('supply.saved.stopFailed'), { id: `supply-stop-${productId}` });
      }
    } catch {
      toast.error(t('supply.saved.stopFailed'), { id: `supply-stop-${productId}` });
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {canChange && (
        <Button
          type="button"
          variant="ghost"
          className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
          onClick={onChangeBlank}
        >
          {t('supply.saved.change')}
        </Button>
      )}
      {canStop && (
        <AlertDialog
          open={open}
          onOpenChange={(next) => {
            // Keep the dialog while the request runs, so its answer is never lost.
            if (!next && pendingRef.current) return;
            setOpen(next);
          }}
        >
          <AlertDialogTrigger asChild>
            {/* Reversible: orange outline, never red (DESIGN.md §13.6). */}
            <Button
              type="button"
              variant="outline"
              className="h-8 gap-1.5 border-orange-300 px-3 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
            >
              {t('supply.saved.stop')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="motion-reduce:animate-none">
            <AlertDialogHeader>
              <AlertDialogTitle className="[overflow-wrap:anywhere]">
                {ownerName ? t('supply.stop.title', { owner: ownerName }) : t('supply.stop.titleUnknown')}
              </AlertDialogTitle>
              <AlertDialogDescription className="leading-normal [overflow-wrap:anywhere]">
                {t('supply.stop.body')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={pending}>{t('supply.stop.keep')}</AlertDialogCancel>
              <Button
                type="button"
                variant="outline"
                className="gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700 motion-reduce:transition-none"
                aria-busy={pending || undefined}
                onClick={() => void stop()}
              >
                {pending && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                {t('supply.saved.stop')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row header chip (UI-SPEC 1.5)
// ---------------------------------------------------------------------------

/**
 * The `lg` mapping-chip slot's "3PL blank" ring pill. An admin's owner name
 * comes from the same `listSharedStockOwners` query (same key) the expanded
 * Tier 1 and Tier 4 make, so the page asks it once; a non-admin makes no read
 * and hears "Prints on a 3PL's blank".
 */
export function SupplyRowChip({ summary, admin }: { summary: ScannedSupply; admin: boolean }) {
  const { t } = useTranslation('productSync');
  const owners = trpc.inventory.listSharedStockOwners.useQuery(undefined, { staleTime: 300_000, enabled: admin });
  const ownerName = admin
    ? (owners.data?.owners.find((o) => o.id === summary.ownerOrganizationId)?.name ?? null)
    : null;
  const attention = summary.attention;
  const owned = ownerName ? t('supply.rowChip.aria', { owner: ownerName }) : t('supply.rowChip.ariaUnknown');
  // Words, never colour alone (DESIGN.md §16): an amber chip says why.
  const aria = attention ? [owned, t('supply.rowChip.attention')].join(' ') : owned;
  return (
    <span
      data-testid="supply-row-chip"
      data-attention={attention ? 'true' : 'false'}
      className={cn(
        'hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 lg:inline-flex',
        attention ? 'bg-amber-100 text-amber-800 ring-amber-300/80' : 'bg-background text-muted-foreground ring-border',
      )}
    >
      <Link2 className={cn('h-2.5 w-2.5 shrink-0', !attention && 'text-threadheads-purple')} aria-hidden="true" />
      <span aria-hidden="true">{t('supply.rowChip.label')}</span>
      <span className="sr-only">{aria}</span>
    </span>
  );
}
